/**
 * `/demo/:scenario` — the blurb for one demo team (#2824).
 *
 * Names the team, its problem and what it needs, then "Start the demo"
 * mints a temporary team from that scenario (`POST /api/demo-sessions`) and
 * lands on Home, where the guide starts.
 *
 * Graceful degradation: when the mint endpoint is not live yet (404/405,
 * the SPA deploys ahead of the backend) the persona picker takes over.
 *
 * #2985 - the access key: the industry and team lists stay open. "Start the
 * demo" asks `ensureDemoAccess()` first, so the key prompt sits right before
 * the mint.
 */
import { ArrowLeft } from "lucide-react";
import { useState } from "react";
import { Link, useParams } from "react-router-dom";

import { ApiError, apiFetch } from "../api/client";
import { Logo } from "../components/Logo";
import { Button } from "../components/ui";
import { type DemoSessionState, leaveDemoOnClick, storeDemoSession } from "../lib/demoSession";
import { useDemoAccess } from "./DemoAccessGate";
import { DemoPage } from "./DemoPage";
import { DemoNeedTiles } from "./demo/DemoNeedArt";
import { industryOf } from "./demo/demoIndustries";
import { displayTeamName, useDemoScenarios } from "./demo/demoScenarios";
import "./marketing/marketing-brand.css";

/** Response shape of `POST /api/demo-sessions` (mirrors the backend
 * `DemoSessionResponse`). */
interface DemoSessionMintResponse {
  token: string;
  account_id: string;
  team_id: string;
  cycle_id: string | null;
  team_name: string;
  expires_at: string;
  solve_time_limit_s: number;
  solves_remaining: number;
}

export function DemoScenarioPage() {
  const { scenario } = useParams<{ scenario: string }>();
  const scenarios = useDemoScenarios();
  const [minting, setMinting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [fallbackToPicker, setFallbackToPicker] = useState(false);

  const card = scenarios.data?.find((item) => item.name === scenario);
  // Back goes to the team's industry page when it has one.
  const industryId = card ? industryOf(card) : undefined;
  const backTo = industryId ? `/demo/industry/${industryId}` : "/demo";

  const { ensureDemoAccess } = useDemoAccess();

  async function startDemo(askAgainOn403 = true): Promise<void> {
    // #2985 — the access key prompt opens here, before the team request.
    if (!(await ensureDemoAccess())) return;
    setMinting(true);
    setErrorMessage(null);
    try {
      const minted = await apiFetch<DemoSessionMintResponse>("/api/demo-sessions", {
        method: "POST",
        body: JSON.stringify({ scenario }),
      });
      const session: DemoSessionState = {
        token: minted.token,
        account_id: minted.account_id,
        team_id: minted.team_id,
        cycle_id: minted.cycle_id,
        team_name: minted.team_name,
        expires_at: minted.expires_at,
        solve_time_limit_s: minted.solve_time_limit_s,
        solves_remaining: minted.solves_remaining,
        ...(scenario ? { scenario } : {}),
      };
      storeDemoSession(session);
      // Hard navigation (not router) so `AuthProvider` re-mounts and
      // picks up the demo-session provider before any data loads.
      window.location.assign("/");
    } catch (err) {
      if (err instanceof ApiError && (err.status === 404 || err.status === 405)) {
        setFallbackToPicker(true);
      } else if (err instanceof ApiError && err.status === 403 && askAgainOn403) {
        // #2985 — the proof no longer holds (the key was revoked, or the mode
        // changed). `apiFetch` dropped it; ask for the key again, once.
        setMinting(false);
        return startDemo(false);
      } else if (err instanceof ApiError) {
        setErrorMessage(err.detail);
      } else {
        setErrorMessage("Something went wrong starting the demo. Try again in a moment.");
      }
      setMinting(false);
    }
  }

  if (fallbackToPicker) {
    return <DemoPage />;
  }

  return (
    <div
      data-marketing-brand=""
      className="dc-page-wash flex min-h-screen flex-col"
      data-testid="demo-scenario-page"
    >
      <nav className="mx-auto flex w-full max-w-2xl items-center justify-between gap-4 px-4 pt-6 sm:px-6">
        <Link
          to={backTo}
          className="inline-flex min-h-tap items-center gap-1.5 text-sm underline-offset-4 hover:underline"
          data-testid="demo-scenario-back"
        >
          <ArrowLeft className="h-4 w-4" aria-hidden="true" />
          Back
        </Link>
        <a href="/" onClick={leaveDemoOnClick} aria-label="Back to the Tarii home page">
          <Logo className="h-8" />
        </a>
      </nav>
      <main className="mx-auto flex w-full max-w-2xl flex-1 flex-col gap-8 px-4 py-10 sm:px-6">
        {scenarios.isPending ? (
          <div className="min-h-[14rem]" aria-hidden="true" data-testid="demo-scenario-loading" />
        ) : null}

        {!scenarios.isPending && !card ? (
          <section className="flex flex-col gap-3" data-testid="demo-scenario-not-found">
            <h1 className="text-2xl font-semibold tracking-tight">We do not have that team</h1>
            <p className="text-sm leading-relaxed">
              <Link to="/demo" className="underline underline-offset-2 hover:text-fg">
                See the demo teams
              </Link>
              .
            </p>
          </section>
        ) : null}

        {card ? (
          <>
            <header className="dc-head flex flex-col gap-3">
              <h1 data-testid="demo-scenario-title">{displayTeamName(card.team_name)}</h1>
              <p className="max-w-prose text-base leading-relaxed">{card.problem}</p>
            </header>

            <section className="flex flex-col gap-3" aria-labelledby="demo-needs">
              <h2 id="demo-needs" className="text-lg font-medium text-fg">
                What this team needs
              </h2>
              <DemoNeedTiles considerations={card.considerations} />
            </section>

            <div className="flex flex-col gap-4">
              <Button
                size="lg"
                className="self-start"
                onClick={() => void startDemo()}
                disabled={minting}
                data-testid="demo-start-button"
              >
                {minting ? "Setting up your team…" : "Start the demo"}
              </Button>
              {errorMessage ? (
                <p className="text-sm" role="alert" data-testid="demo-start-error">
                  {errorMessage}
                </p>
              ) : null}
            </div>
          </>
        ) : null}
      </main>
    </div>
  );
}
